import Head from 'next/head';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '../../apollo-client';
import NextNprogress from 'nextjs-progressbar';

import metaRobots from '../../helpers/metaRobots';

import Layout from '../../components/Layout';
import FaqWithButtons from '../../components/UI/FaqWithButtons';

import sharecardImage from '../../public/images/sharecard.jpg';

import PlusPeople from '../../components/Icons/PlusPeople';
import Handshake from '../../components/Icons/Handshake';
import LaptopWithPeople from '../../components/Icons/LaptopWithPeople';
import Laptop from '../../components/Icons/Laptop';
import sitePath from '../../helpers/sitePath';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { localeCapitalized, localizedCategory } from '../../helpers/locale';

const FAQS = gql`
  query Faqs($categoryName: String!, $locale: LanguageCodeFilterEnum!) {
    faqs(
      first: 1000
      where: {
        categoryName: $categoryName
        orderby: { field: MENU_ORDER, order: ASC }
        language: $locale
      }
    ) {
      nodes {
        title
        faq {
          items {
            question
            answer
          }
        }
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
      }
    }
  }
`;

export async function getServerSideProps({ locale }) {
  const apolloClient = initializeApollo();

  await apolloClient.query({
    query: FAQS,
    variables: {
      categoryName: localizedCategory('demand', locale),
      locale: localeCapitalized(locale),
    },
  });

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function Faq() {
  const router = useRouter();

  const { t } = useTranslation('faq');

  const meta = {
    title: 'Frequently Asked Questions(FAQ) for Businesses - Terminal.io',
    description: '',
  };

  const buttons = [
    {
      icon: <PlusPeople key="1" />,
      text: t('hiring'),
      titleIcon: <Handshake key="1" />,
      url: '/faq',
      active: true,
    },
    {
      icon: <Laptop key="1" />,
      text: t('developer'),
      titleIcon: <LaptopWithPeople key="2" />,
      url: '/faq/engineers',
      active: false,
    },
  ];

  const footer = {
    longText: false,
    text: 'Find the scale you need. Build your team today.',
    button: {
      url: '',
      text: 'Build My Team',
    },
  };

  // Client side Apollo
  const { loading, error, data } = useQuery(FAQS, {
    variables: {
      categoryName: localizedCategory('demand', router.locale),
      locale: localeCapitalized(router.locale),
    },
  });

  // CSR loading
  if (loading) {
    return (
      <NextNprogress
        color="#25383F"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
        options={{
          showSpinner: false,
        }}
      />
    );
  }

  const faqs = data?.faqs?.nodes ?? null;
  const allFaqs = faqs?.flatMap((faq) => faq.faq.items);
  const mainEntity = allFaqs?.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  }));

  const structuredData = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  });

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="ogdescription" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />

        {faqs && faqs.length > 0 && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structuredData }} />
        )}
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        <FaqWithButtons buttons={buttons} faqs={faqs} />
      </Layout>
    </>
  );
}
